import { Component, computed, effect, model, output, signal } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  DsButtonModule,
  DsFormFieldModule,
  DsIconModule,
  DsModalModule,
  DsSelectModule,
  SelectListOption,
} from '@bmw-ds/components';
import { TranslateModule } from '@ngx-translate/core';
import { ActionPlanItem } from 'src/app/shared/enums/action-plan-item.enum';
import { ComfyAgents } from 'src/app/shared/enums/comfy-agents.enum';
import { PureQueue } from 'src/app/shared/enums/pure-queue.enum';
import { TramoActions } from 'src/app/shared/enums/tramo-actions.enum';
import { QueueFileUploadParameters } from 'src/app/shared/models/backoffice/queue-cases/queue-file-upload-parameters.model';

@Component({
  selector: 'pure-upload-queue-file',
  imports: [
    DsModalModule,
    TranslateModule,
    DsFormFieldModule,
    DsSelectModule,
    FormsModule,
    DsButtonModule,
    DsIconModule,
    ReactiveFormsModule,
  ],
  templateUrl: './upload-queue-file.component.html',
  styleUrl: './upload-queue-file.component.scss',
})
export class UploadQueueFileComponent {
  selectedFile = model<File | undefined>(undefined);
  selectedQueue = model<PureQueue | undefined>(undefined);
  selectedAgent = model<ComfyAgents | undefined>(undefined);
  selectedAction = model<TramoActions | undefined>(undefined);

  closed = output<void>();
  queueFileSelected = output<QueueFileUploadParameters>();

  modalIsOpen = signal<boolean>(true);

  protected readonly interfaceOptions = signal<SelectListOption[]>(this.buildInterfaceOptions());
  protected readonly agentOptions = signal<SelectListOption[]>(this.buildAgentOptions());
  protected readonly tramoActionsOptions = signal<SelectListOption[]>(this.buildTramoAactionsOptions());

  protected readonly isComfyQueue = computed(
    () =>
      this.selectedQueue() === PureQueue.COMFY_ACTION_STATUS ||
      this.selectedQueue() === PureQueue.COMFY_TRIGGER_STATUS
  );

  protected readonly disableSave = computed(
    () =>
      !this.selectedFile() ||
      !this.selectedQueue() ||
      (this.isComfyQueue() &&
        (!this.selectedAgent() || (this.selectedAgent() === ComfyAgents.TRAMO && !this.selectedAction())))
  );

  protected readonly pureQueueValue = PureQueue;
  protected readonly comfyAgents = ComfyAgents;

  protected fileName = new FormControl('');

  constructor() {
    effect(() => {
      this.selectedQueue();
      this.selectedAgent.set(undefined);
      this.selectedAction.set(undefined);
    });
  }

  private buildInterfaceOptions(): SelectListOption[] {
    return [
      { id: PureQueue.COMFY_ACTION_STATUS, label: 'COMFY' } as SelectListOption,
      { id: PureQueue.RSU, label: 'RSU' } as SelectListOption,
      { id: PureQueue.RITA, label: 'RITA' } as SelectListOption,
    ];
  }

  private buildAgentOptions(): SelectListOption[] {
    const agents = [ComfyAgents.TRAMO, ComfyAgents.PROVISIONING, ComfyAgents.RSU];

    return agents.map((value) => ({
      id: value,
      label: value,
    } as SelectListOption));
  }

  private buildTramoAactionsOptions(): SelectListOption[] {
    const optionsList: SelectListOption[] = [];
    Object.values(TramoActions).forEach((value) => {
      optionsList.push({
        id: value,
        label: value,
      } as SelectListOption);
    });
    return optionsList;
  }

  modalDismissed(): void {
    this.modalIsOpen.set(false);
  }

  uploadQueueFile(): void {
    let actionItem = undefined;
    let resolvedQueue = this.selectedQueue();

    if (this.isComfyQueue()) {
      if (this.selectedAgent() === ComfyAgents.RSU) {
        resolvedQueue = PureQueue.COMFY_TRIGGER_STATUS;
        actionItem = ActionPlanItem.USE_CASE;
      } else if (this.selectedAgent() === ComfyAgents.TRAMO) {
        resolvedQueue = PureQueue.COMFY_ACTION_STATUS;
        if (this.selectedAction() === TramoActions.ENABLE) {
          actionItem = ActionPlanItem.TRAMO_ENABLED;
        } else {
          actionItem = ActionPlanItem.TRAMO_DISABLED;
        }
      } else if (this.selectedAgent() === ComfyAgents.PROVISIONING) {
        resolvedQueue = PureQueue.COMFY_ACTION_STATUS;
        actionItem = ActionPlanItem.PROVISIONING;
      }
    } else if (resolvedQueue === PureQueue.RSU) {
      actionItem = ActionPlanItem.USE_CASE;
    }

    const queueFileParameters = {
      selectedFile: this.selectedFile(),
      selectedQueue: resolvedQueue,
      selectedAction: actionItem,
    } as QueueFileUploadParameters;

    this.queueFileSelected.emit(queueFileParameters);
    this.modalDismissed();
  }

  updateSelectedFile(event: Event): void {
    const inputFiles = (event.target as HTMLInputElement).files;

    if (inputFiles && inputFiles?.length > 0) {
      this.selectedFile.set(inputFiles[0]);
    }
  }
}
